import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Input from "../../input/Input";
import Dropdown from "../../input/Dropdown";
import Button from "../../input/Button";
import { getBulkDropdownData, getDropdownData } from "../../../api/keyvalue";
import { updateVehicleCheckup } from "../../../api/vehicleModificationApi";
import useUserCompany from "../../../hooks/useUserCompany";
import { toast } from "react-toastify";

const VehicleCheckupChange = ({ selectedIds, onSuccess, onClose }) => {
  const user = useSelector((state) => state.user);
  const company = useUserCompany();
  const companyId = company?.comId;

  const today = new Date().toISOString().split("T")[0];

  const [vckType, setVckType] = useState("");
  const [vckDate, setVckDate] = useState(today);
  const [vckExpiry, setVckExpiry] = useState("");
  const [vckCondition, setVckCondition] = useState("");
  const [vckActive, setVckActive] = useState(1);
  const [modificationRemark, setModificationRemark] = useState("");
  const [vckTypeOpt, setVckTypeOpt] = useState([]);
  const [conditionOpt, setConditionOpt] = useState([]);
  const [activeOpt, setActiveOpt] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      const vckTypeData = await getDropdownData("veh_check", "", [
        "keyValue2",
        "keyValue3",
      ]);
      setVckTypeOpt(vckTypeData);

      const { veh_check_condition: conditionOptions, active: activeOptions } =
        await getBulkDropdownData([
          { key: "veh_check_condition" },
          { key: "active" },
        ]);
      setConditionOpt(conditionOptions);
      setActiveOpt(activeOptions);
    })();
  }, []);

  const clearError = (field) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!vckType) newErrors.vckType = "Checkup Type is required";
    if (!vckDate) newErrors.vckDate = "Checkup Date is required";
    if (!vckExpiry)
      newErrors.vckExpiry = "Expiry / Next Action Date is required";
    if (!vckCondition) newErrors.vckCondition = "Condition is required";
    if (!vckActive && vckActive !== 0)
      newErrors.vckActive = "Active is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const request = {
      vehIds: selectedIds,
      vckType: vckType,
      vckDate: vckDate,
      vckExpiry: vckExpiry,
      vckCondition: vckCondition,
      vckActive: vckActive,
      modificationRemark: modificationRemark,
    };

    try {
      setIsSubmitting(true);
      const response = await updateVehicleCheckup(
        request,
        user.username,
        companyId,
      );

      const allSuccess = response.every((r) => r.success);
      const anySuccess = response.some((r) => r.success);
      const failed = response.filter((r) => !r.success);

      if (allSuccess) {
        toast.success("Vehicle checkup updated successfully!", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else if (anySuccess) {
        toast.success(
          "Vehicle checkup updated successfully for some vehicles!",
          {
            autoClose: 5000,
            containerId: "Modal",
          },
        );
        failed.forEach((f) => {
          toast.error(f.message, {
            autoClose: 8000,
            containerId: "Modal",
          });
        });
       
      } else {
        failed.forEach((f) => {
          toast.error(f.message, {
            autoClose: 8000,
            containerId: "Modal",
          });
        });
      }
    } catch (error) {
      console.log("Error updating vehicle checkup", error);
      toast.error("An error occurred. Please try again.", {
        autoClose: 5000,
        containerId: "Modal",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setVckType("");
    setVckDate(today);
    setVckExpiry("");
    setVckCondition("");
    setVckActive(1);
    setModificationRemark("");
    setErrors({});
  };

  return (
    <div>
      <form className="ui form">
        <div className="ui segment">
          <h4 className="ui dividing header">Update Vehicle Checkup</h4>
          <div className="two fields">
            <Dropdown
              label="Checkup Type"
              required={true}
              options={vckTypeOpt}
              value={vckType}
              placeholder="Select Checkup Type"
              onChange={(e, { value }) => {
                setVckType(value);
                clearError("vckType");
              }}
              error={errors.vckType}
            />
            <Input
              label="Checkup Date"
              required={true}
              type="date"
              value={vckDate}
              onChange={(e) => {
                setVckDate(e.target.value);
                setVckExpiry("");
                clearError("vckDate");
              }}
              error={errors.vckDate}
            />
          </div>
          <div className="two fields">
            <Input
              label="Expiry / Next Action Date"
              required={true}
              type="date"
              value={vckExpiry}
              min={vckDate}
              onChange={(e) => {
                setVckExpiry(e.target.value);
                clearError("vckExpiry");
              }}
              error={errors.vckExpiry}
            />
            <Dropdown
              label="Condition"
              required={true}
              options={conditionOpt}
              value={vckCondition}
              placeholder="Select Condition"
              onChange={(e, { value }) => {
                setVckCondition(value);
                clearError("vckCondition");
              }}
              error={errors.vckCondition}
            />
          </div>
          <div className="two fields">
            <Dropdown
              label="Active"
              required={true}
              options={activeOpt}
              value={vckActive}
              placeholder="Select Active"
              onChange={(e, { value }) => {
                setVckActive(value);
                clearError("vckActive");
              }}
              error={errors.vckActive}
            />
            <Input
              label="Remark"
              placeholder="Enter modification remark"
              value={modificationRemark}
              onChange={(e) => setModificationRemark(e.target.value)}
            />
          </div>
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
          >
            <Button
              template="reset"
              onClick={handleReset}
              disabled={isSubmitting}
            />
            <Button
              template="submit"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default VehicleCheckupChange;
